import Dexie, { type EntityTable } from 'dexie';
import { LANDMARK_COUNT } from '@erectus/shared';
import type { FrameRecord, FrameState, SessionSummary } from '@erectus/shared';

/**
 * On-device session store (docs/ARCHITECTURE.md §1.6).
 *
 * Frames live in IndexedDB while a session runs, so a 3-hour session never holds
 * 30k objects in memory and a crashed tab can still produce a report.
 * Nothing here ever leaves the device unless the user opted in (Phase 5).
 * Frames are kept for DEVICE_FRAME_RETENTION_DAYS after a session ends, for
 * everyone: a long daily habit would otherwise grow by hundreds of MB a month.
 * Shared (opted-in) sessions stay replayable from the account after that; a
 * session whose keypoint upload is still pending is kept until it goes.
 */

/** Days a session's frames stay on the device after it ends (DATA §1). */
export const DEVICE_FRAME_RETENTION_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export function purgeAfterFor(endedAt: number): number {
  return endedAt + DEVICE_FRAME_RETENTION_DAYS * DAY_MS;
}

export interface StoredFrame {
  id?: number;
  sessionId: string;
  t: number;
  state: FrameState;
  /** Rows written before thresholds v2 carry `esa` instead and read back as null. */
  cva?: number | null;
  cha?: number | null;
  trunk: number | null;
  prot: number | null;
  hip: number | null;
  knee: number | null;
  /** Quantized landmarks — see `quantize()`. */
  lm: Int16Array;
}

export interface StoredSession {
  id: string;
  uid: string;
  startedAt: number;
  endedAt: number | null;
  status: 'recording' | 'ended' | 'summarized' | 'synced';
  plannedMin: number;
  sampleFps: number;
  poseModel: 'lite' | 'full';
  framingScore: number;
  summary: SessionSummary | null;
  /** Set once the summary reached Firestore. */
  syncedAt: number | null;
  /** Set once the opt-in keypoint file reached Storage (Phase 5). */
  uploadedAt: number | null;
  /**
   * When this session's frames leave the device (purgeAfterFor). Set to
   * Number.MAX_SAFE_INTEGER once they are gone.
   */
  purgeAfter: number;
}

/**
 * The ten-minute skeleton snapshots of a finished session (scoring/hourline).
 * Kept apart from `frames` so they outlive the 7-day frame purge and
 * "Delete replays": a handful of
 * landmark sets per session, on this device only, never uploaded — landmarks
 * leave the device only with the separate data opt-in (CLAUDE.md principle 3).
 * Deleted with the session and with the account.
 */
export interface StoredSnapshot {
  id?: number;
  sessionId: string;
  t: number;
  blockStart: number;
  blockEnd: number;
  blockScore: number;
  blockGood: number;
  state: FrameState;
  /** Absent on snapshots from before thresholds v2. */
  cva?: number | null;
  trunk: number | null;
  lm: Int16Array;
}

/** Firestore writes that failed while offline, retried on the next app start. */
export interface OutboxItem {
  id?: number;
  /** 'keypoints': an opted-in session's .bin upload that failed, retried until it goes. */
  kind: 'session' | 'feedback' | 'calibration' | 'keypoints';
  sessionId: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
}

export class ErectusDb extends Dexie {
  frames!: EntityTable<StoredFrame, 'id'>;
  sessions!: EntityTable<StoredSession, 'id'>;
  outbox!: EntityTable<OutboxItem, 'id'>;
  snapshots!: EntityTable<StoredSnapshot, 'id'>;

  constructor() {
    super('erectus');
    this.version(1).stores({
      frames: '++id, sessionId, [sessionId+t]',
      sessions: 'id, uid, startedAt, status, purgeAfter',
      outbox: '++id, kind, createdAt',
    });
    this.version(2).stores({
      snapshots: '++id, sessionId, [sessionId+t]',
    });
    // 2026-10-07, briefly: frames kept until the user deleted them (cancelled every purge).
    this.version(3)
      .stores({})
      .upgrade((tx) =>
        tx
          .table('sessions')
          .toCollection()
          .modify((session: StoredSession) => {
            session.purgeAfter = Number.MAX_SAFE_INTEGER;
          }),
      );
    // Back to the 7-day device window for everyone, counted from each session's end.
    this.version(4)
      .stores({})
      .upgrade((tx) =>
        tx
          .table('sessions')
          .toCollection()
          .modify((session: StoredSession) => {
            session.purgeAfter = purgeAfterFor(session.endedAt ?? session.startedAt);
          }),
      );
  }
}

export const db = new ErectusDb();

export const FRAME_STRIDE = 4;
export const QUANT_XYZ = 10_000;
export const QUANT_VIS = 100;

/**
 * Quantize a landmark frame to Int16: coordinates ×10000, visibility ×100.
 * 33 × 4 × 2 bytes = 264 B per frame, so a 1-hour session at 3 fps is ~2.8 MB
 * raw and roughly 1 MB gzipped (ARCHITECTURE §1.6).
 */
export function quantize(landmarks: readonly { x: number; y: number; z: number; visibility: number }[]): Int16Array {
  const out = new Int16Array(LANDMARK_COUNT * FRAME_STRIDE);
  for (let i = 0; i < LANDMARK_COUNT; i += 1) {
    const lm = landmarks[i];
    if (!lm) continue;
    const base = i * FRAME_STRIDE;
    out[base] = clampInt16(lm.x * QUANT_XYZ);
    out[base + 1] = clampInt16(lm.y * QUANT_XYZ);
    out[base + 2] = clampInt16(lm.z * QUANT_XYZ);
    out[base + 3] = clampInt16(lm.visibility * QUANT_VIS);
  }
  return out;
}

export function dequantize(frame: Int16Array): Array<{ x: number; y: number; z: number; visibility: number }> {
  const out: Array<{ x: number; y: number; z: number; visibility: number }> = [];
  for (let i = 0; i < LANDMARK_COUNT; i += 1) {
    const base = i * FRAME_STRIDE;
    out.push({
      x: (frame[base] ?? 0) / QUANT_XYZ,
      y: (frame[base + 1] ?? 0) / QUANT_XYZ,
      z: (frame[base + 2] ?? 0) / QUANT_XYZ,
      visibility: (frame[base + 3] ?? 0) / QUANT_VIS,
    });
  }
  return out;
}

function clampInt16(v: number): number {
  return Math.max(-32768, Math.min(32767, Math.round(v)));
}

export async function appendFrame(sessionId: string, frame: FrameRecord): Promise<void> {
  await db.frames.add({
    sessionId,
    t: frame.t,
    state: frame.state,
    cva: frame.cva,
    cha: frame.cha,
    trunk: frame.trunk,
    prot: frame.prot,
    hip: frame.hip,
    knee: frame.knee,
    lm: frame.lm,
  });
}

export async function loadFrames(sessionId: string): Promise<FrameRecord[]> {
  const rows = await db.frames.where('sessionId').equals(sessionId).sortBy('t');
  return rows.map((row) => ({
    t: row.t,
    state: row.state,
    cva: row.cva ?? null,
    cha: row.cha ?? null,
    trunk: row.trunk,
    prot: row.prot,
    hip: row.hip,
    knee: row.knee,
    lm: row.lm,
  }));
}

/**
 * Pack a session's frames into the `.bin` layout the training pipeline reads.
 * Header: magic 'ERC1', frameCount u32, landmarkCount u16, stride u16, fps u16.
 * Then per frame: t (u32 ms), state (u8), padding (u8), landmarks (int16 × 132).
 */
export const BIN_MAGIC = 0x45524331; // 'ERC1'

const STATE_CODE: Record<FrameState, number> = {
  good: 0,
  forwardHead: 1,
  slouch: 2,
  recline: 3,
  invalid: 4,
};

export function packFrames(frames: readonly FrameRecord[], sampleFps: number): ArrayBuffer {
  const headerBytes = 12;
  const perFrame = 6 + LANDMARK_COUNT * FRAME_STRIDE * 2;
  const buffer = new ArrayBuffer(headerBytes + frames.length * perFrame);
  const view = new DataView(buffer);
  view.setUint32(0, BIN_MAGIC);
  view.setUint32(4, frames.length);
  view.setUint16(8, LANDMARK_COUNT);
  view.setUint16(10, sampleFps);

  let offset = headerBytes;
  for (const frame of frames) {
    view.setUint32(offset, Math.max(0, Math.round(frame.t)));
    view.setUint8(offset + 4, STATE_CODE[frame.state]);
    view.setUint8(offset + 5, 0);
    let p = offset + 6;
    for (let i = 0; i < LANDMARK_COUNT * FRAME_STRIDE; i += 1) {
      view.setInt16(p, frame.lm[i] ?? 0);
      p += 2;
    }
    offset += perFrame;
  }
  return buffer;
}

const STATE_BY_CODE: FrameState[] = ['good', 'forwardHead', 'slouch', 'recline', 'invalid'];

/**
 * Read a `.bin` back: what the replay needs from a shared session file on any
 * device. The file carries time, state and landmarks only — the angles are
 * worked out again by the caller.
 */
export function unpackFrames(buffer: ArrayBuffer): { sampleFps: number; frames: Array<Pick<FrameRecord, 't' | 'state' | 'lm'>> } {
  const view = new DataView(buffer);
  if (buffer.byteLength < 12 || view.getUint32(0) !== BIN_MAGIC) throw new Error('Not an Erectus keypoint file');
  const count = view.getUint32(4);
  const landmarks = view.getUint16(8);
  const sampleFps = view.getUint16(10);
  const values = landmarks * FRAME_STRIDE;
  const perFrame = 6 + values * 2;
  if (buffer.byteLength < 12 + count * perFrame) throw new Error('Keypoint file is truncated');

  const frames: Array<Pick<FrameRecord, 't' | 'state' | 'lm'>> = [];
  let offset = 12;
  for (let n = 0; n < count; n += 1) {
    const lm = new Int16Array(values);
    for (let i = 0; i < values; i += 1) lm[i] = view.getInt16(offset + 6 + i * 2);
    frames.push({ t: view.getUint32(offset), state: STATE_BY_CODE[view.getUint8(offset + 4)] ?? 'invalid', lm });
    offset += perFrame;
  }
  return { sampleFps, frames };
}

/** gunzip via DecompressionStream. */
export async function gunzip(data: ArrayBuffer): Promise<ArrayBuffer> {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

/** gzip via CompressionStream; returns the raw buffer when unavailable. */
export async function gzip(data: ArrayBuffer): Promise<Blob> {
  if (typeof CompressionStream === 'undefined') return new Blob([data]);
  const stream = new Blob([data]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
}

/**
 * Delete on-device frames past their window (DATA §1). Sessions with a keypoint
 * upload still queued are skipped: their frames are the only copy until it goes.
 */
export async function purgeExpiredFrames(now = Date.now()): Promise<number> {
  const expired = await db.sessions.where('purgeAfter').below(now).toArray();
  if (expired.length === 0) return 0;
  const pending = new Set(
    (await db.outbox.where('kind').equals('keypoints').toArray()).map((item) => item.sessionId),
  );
  let removed = 0;
  for (const session of expired) {
    if (pending.has(session.id)) continue;
    removed += await db.frames.where('sessionId').equals(session.id).delete();
    await db.sessions.update(session.id, { purgeAfter: Number.MAX_SAFE_INTEGER });
  }
  return removed;
}

/**
 * What the replays take on this device: sessions that still have frames, and
 * roughly how many bytes (each frame is 33 landmarks × 4 int16, plus a little).
 */
export async function replayStorage(): Promise<{ sessions: number; bytes: number }> {
  const sessionIds = new Set<string>();
  let frames = 0;
  await db.frames.orderBy('sessionId').eachKey((key) => {
    sessionIds.add(String(key));
    frames += 1;
  });
  return { sessions: sessionIds.size, bytes: frames * (LANDMARK_COUNT * FRAME_STRIDE * 2 + 64) };
}

/**
 * Settings → "Delete replays on this phone": every frame goes, the reports
 * stay (their summaries are in the account, the ten-minute cards in `snapshots`).
 */
export async function deleteReplayFrames(): Promise<void> {
  await db.frames.clear();
}

export async function saveSnapshots(sessionId: string, snapshots: Omit<StoredSnapshot, 'id' | 'sessionId'>[]): Promise<void> {
  await db.snapshots.where('sessionId').equals(sessionId).delete();
  await db.snapshots.bulkAdd(snapshots.map((s) => ({ ...s, sessionId })));
}

export async function loadSnapshots(sessionId: string): Promise<StoredSnapshot[]> {
  return db.snapshots.where('[sessionId+t]').between([sessionId, -Infinity], [sessionId, Infinity]).toArray();
}

export async function deleteLocalSession(sessionId: string): Promise<void> {
  await db.frames.where('sessionId').equals(sessionId).delete();
  await db.snapshots.where('sessionId').equals(sessionId).delete();
  await db.sessions.delete(sessionId);
  // A session still waiting to sync must not come back on the next app start.
  await db.outbox.filter((item) => item.sessionId === sessionId).delete();
}

/** Wipe every local trace — used by account deletion (LEGAL §8). */
export async function wipeLocalData(): Promise<void> {
  await db.frames.clear();
  await db.snapshots.clear();
  await db.sessions.clear();
  await db.outbox.clear();
}
